import { IPage } from '@aon/aon-tws-common-ui';
import { IHierarchyVm } from '@aon/tws-common-ui-hierarchy';
import { HashTable, IHierarchySearchResult, INodeVm } from '@aon/tws-common-ui-hierarchy/build/lib/types';
import axios from 'axios';
import Constants from '../constants';
import { IHierarchyMetadata, IHierarchyMetrics, IHrisAttributeModel, IHrisFileListItem, IHrisFileSummary, IProfile } from '../models';
import { IPerson } from '../models/GroupAccessOwners/PersonVm';
import { IQueryOptions } from '../models/GroupAccessOwners/ServiceInterfaces';
import IHierarchyGroupAccess from '../models/IHierarchyGroupAccess';
import { IHrisColumnMapping } from '../models/IHrisAttributeModel';
import { HtmlUtil } from '../utilities';

export async function getHrisListForClient(): Promise<IHrisFileListItem[]> {
	const res = await axios.get(`${Constants.ORGMANAGER_API_BASE_URL}/people`, Constants.noCacheConfig);
	return res.data;
}

export async function getHrisFileSummary(hrisId: string): Promise<IHrisFileSummary> {
	const res = await axios.get(
		`${Constants.ORGMANAGER_API_BASE_URL}/people/${hrisId}/${Constants.HIERARCHY_HRIS_SUMMARY}`,
		Constants.noCacheConfig,
	);
	return res.data;
}

export async function getHrisAttributes(hrisId: string): Promise<IHrisAttributeModel[]> {
	const res = await axios.get(
		`${Constants.ORGMANAGER_API_BASE_URL}/people/${hrisId}/${Constants.HIERARCHY_ATTRIBUTES}`,
		Constants.noCacheConfig,
	);
	return res.data;
}

export async function getHrisAttributeMappings(forHris: string): Promise<IHrisColumnMapping[]> {
	const res = await axios.get(`${Constants.ORGMANAGER_API_BASE_URL}/people/${forHris}/attributemappings`, Constants.noCacheConfig);
	return res.data;
}

export async function getHrisAttributeValues(hrisId: string, attributeName: string): Promise<{ value: string | null; count: number }[]> {
	const res = await axios.get(
		`${Constants.ORGMANAGER_API_BASE_URL}/people/${hrisId}/attributes/valueCounts?attributeName=${attributeName}`,
		Constants.noCacheConfig,
	);
	return res.data;
}

export async function getHrisHierarchies(hrisId: string): Promise<IHierarchyMetadata[]> {
	const res = await axios.get(`${Constants.ORGMANAGER_API_BASE_URL}/hrishierarchy?hrisId=${hrisId}`, Constants.noCacheConfig);
	return res.data;
}

export async function getHierarchies(structureOnlyHierarchy: boolean): Promise<IHierarchyMetadata[]> {
	const res = await axios.get(
		`${Constants.ORGMANAGER_API_BASE_URL}/hierarchymetadata?structureOnlyHierarchy=${structureOnlyHierarchy}`,
		Constants.noCacheConfig,
	);
	return res.data;
}

export async function getDisconnectedHierarchiesListForClient(): Promise<IHierarchyMetadata[]> {
	const res = await axios.get(`${Constants.ORGMANAGER_API_BASE_URL}/hierarchymetadata/disconnected`, Constants.noCacheConfig);
	return res.data;
}

export async function getHierarchy(params: {
	hierarchyId: string;
	nodeId?: string;
	expandPath?: boolean;
	includeOwners?: boolean;
}): Promise<IHierarchyVm> {
	const { hierarchyId, nodeId, expandPath, includeOwners } = params;

	const route = () => {
		if (typeof nodeId === 'undefined') {
			return `hierarchies/${hierarchyId}`;
		} else {
			return `hierarchies/${hierarchyId}/nodes/${HtmlUtil.encodeUri(nodeId)}`;
		}
	};

	const res = await axios.get(`${Constants.ORGMANAGER_API_BASE_URL}/${route()}`, {
		...Constants.noCacheConfig,

		params: {
			expandPath,
			includeOwners,
		},
	});
	return res.data;
}

export async function getHierarchyMetrics(hierarchyId: string, nodeIds: string[]): Promise<HashTable<IHierarchyMetrics>> {
	const res = await axios.post(`${Constants.ORGMANAGER_API_BASE_URL}/hierarchies/${hierarchyId}/metrics`, nodeIds);
	return res.data;
}

export async function fetchNodesWithRelationship(
	relationshipType: 'Belongs To',
	toNode: string,
	inHierarchy: string,
	offset?: number,
	limit?: number,
): Promise<IPage<INodeVm>> {
	const res = await axios.get<INodeVm[]>(
		`${Constants.ORGMANAGER_API_BASE_URL}/hierarchies/${inHierarchy}/nodes/${toNode}/relationships/relationship`,
		{
			params: {
				type: (relationshipType === 'Belongs To' && 'belongs_to') || '',
				offset,
				limit,
			},
		},
	);

	return {
		items: res.data,
		pageRange: JSON.parse(res.headers.range),
	};
}

export async function fetchOrphanNodes(ofType: 'Person', forHierarchy: string, offset?: number, limit?: number): Promise<IPage<INodeVm>> {
	const res = await axios.get<INodeVm[]>(`${Constants.ORGMANAGER_API_BASE_URL}/hierarchies/${forHierarchy}/orphanNodes`, {
		params: {
			type: ofType,
			offset,
			limit,
		},
	});

	return {
		items: res.data,
		pageRange: JSON.parse(res.headers.range),
	};
}

export async function fetchAncestorOf(nodeIds: string[], forHierarchy: string): Promise<IHierarchyVm> {
	const res = await axios.post(`${Constants.ORGMANAGER_API_BASE_URL}/hierarchies/${forHierarchy}/ancestor`, nodeIds);
	return res.data;
}

export async function searchInHierarchy(hierarchyId: string, searchParams: string): Promise<IHierarchySearchResult[]> {
	const res = await axios.get(`${Constants.ORGMANAGER_API_BASE_URL}/hierarchies/${hierarchyId}/search?searchParam=${searchParams}`);
	return res.data;
}

export async function getHierarchyGroupAccesses(hrisId: string): Promise<IHierarchyGroupAccess[]> {
	const res = await axios.get<IHierarchyGroupAccess[]>(
		`${Constants.ORGMANAGER_API_BASE_URL}/people/${hrisId}/hierarchies/group-access`,
		Constants.noCacheConfig,
	);

	return res.data;
}

export async function getProfiles(forHris: string): Promise<IProfile[]> {
	const res = await axios.get(
		`${Constants.ORGMANAGER_API_BASE_URL}/people/${forHris}/profiles?includeHierarchyOwnership=true`,
		Constants.noCacheConfig,
	);

	return res.data;
}

export async function fetchGroupAccessOwners(queryOptions: IQueryOptions, forHris: string, forHierarchy?: string): Promise<IPage<IPerson>> {
	const route = () => {
		if (typeof forHierarchy === 'undefined') {
			return `people/${forHris}/group-access/owners`;
		} else {
			return `people/${forHris}/hierarchies/${forHierarchy}/group-access/owners`;
		}
	};

	const response = await axios.get(`${Constants.ORGMANAGER_API_BASE_URL}/${route()}`, {
		params: {
			queryInfo: JSON.stringify(queryOptions.queryExpressions),
			...queryOptions.page,
		},
	});

	return response.data;
}
